import * as THREE from 'three';
import { Entity, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';

/**
 * Free-falling bomb dropped by bombers. Inherits the bomber's horizontal velocity at
 * spawn, then falls under gravity until it hits the ground. On impact it spawns a big
 * fireball + smoke plume, plays a boom, and craters the buildings within blast radius
 * (one at a time, like strafing) — so a single bomb takes a chunk out of a village, not
 * the whole thing. It takes several runs to level a village, rewarding interception.
 */
const GRAVITY = 26;      // u/s² downward
const BLAST_RADIUS = 75; // buildings within this of the impact are destroyed (~3 per direct hit,
                         // ~4+ runs to level a village — measured against the village layouts)

/** A WW1 aerial bomb (P.u.W.-style): a streamlined teardrop body — fat toward the nose, tapering to
 *  a slender tail — with a cruciform tail-fin assembly, a brass nose fuze and a suspension lug. Built
 *  with the nose along +Z — Object3D.lookAt points +Z at the target, and Bomb.update aims at the
 *  velocity, so the bomb falls nose-first. */
export function makeBombMesh(): THREE.Group {
  const g = new THREE.Group();
  // Low-poly faceted look (flatShading) to match the rest of the game's art.
  const steel = new THREE.MeshStandardMaterial({ color: 0x474b40, roughness: 0.6, metalness: 0.45, flatShading: true });
  const dark = new THREE.MeshStandardMaterial({ color: 0x2a2c27, roughness: 0.8, metalness: 0.3, flatShading: true });
  const brass = new THREE.MeshStandardMaterial({ color: 0x8a6a2e, roughness: 0.45, metalness: 0.6, flatShading: true });

  // Faceted teardrop body — few profile points, few radial segments → a chunky low-poly bomb.
  const profile = ([
    [0.04, -1.0], [0.20, -0.55], [0.42, -0.1], [0.50, 0.25], [0.34, 0.66], [0.12, 0.95], [0.0, 1.05],
  ] as [number, number][]).map(([r, y]) => new THREE.Vector2(r, y));
  const body = new THREE.Mesh(new THREE.LatheGeometry(profile, 9), steel);
  body.rotation.x = Math.PI / 2; // nose (+Y) → +Z
  g.add(body);

  // Brass nose fuze.
  const fuze = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.11, 0.16, 6), brass);
  fuze.rotation.x = Math.PI / 2;
  fuze.position.set(0, 0, 1.08);
  g.add(fuze);

  // Cruciform tail fins (flat plates) at the tail (-Z).
  for (let i = 0; i < 4; i++) {
    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.04, 0.6), dark);
    fin.position.set(0.3, 0, -0.74);
    const pivot = new THREE.Group();
    pivot.add(fin);
    pivot.rotation.z = (i * Math.PI) / 2;
    g.add(pivot);
  }

  // Suspension lug (how it hung in the rack).
  const lug = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.16, 0.18), steel);
  lug.position.set(0, 0.48, -0.05);
  g.add(lug);

  g.scale.setScalar(0.8); // a small object
  g.traverse((o) => { (o as THREE.Mesh).castShadow = false; (o as THREE.Mesh).receiveShadow = false; });
  return g;
}

export class Bomb extends Entity {
  vel: THREE.Vector3;
  smokeTimer = 0;
  constructor(pos: THREE.Vector3, initialVel: THREE.Vector3, ownerId: number) {
    // Use the 'fx' kind so the collision pass ignores bombs entirely — they handle their
    // own ground-check and aren't shootable / rammable.
    super('fx');
    this.ownerId = ownerId;
    this.radius = 1.0;
    this.hp = 1;
    this.vel = initialVel.clone();
    this.group.position.copy(pos);
    this.group.add(makeBombMesh()); // WW1 finned aerial bomb (nose at -Z, oriented to velocity)
  }

  update(ctx: EntityCtx): void {
    const dt = ctx.dt;
    // Integrate velocity + gravity.
    this.vel.y -= GRAVITY * dt;
    this.group.position.addScaledVector(this.vel, dt);
    // Orient so the nose follows the velocity direction (so the bomb tilts as it falls).
    if (this.vel.lengthSq() > 0.001) {
      const lookAt = this.group.position.clone().add(this.vel);
      this.group.lookAt(lookAt);
    }
    // Trail smoke
    this.smokeTimer += dt;
    if (this.smokeTimer >= 0.08) {
      this.smokeTimer = 0;
      new Explosion(this.group.position.clone(), 4, 1.4, 'smoke')
        .setDrift(new THREE.Vector3(0, 4, 0))
        .addTo(ctx);
    }
    // Detonate at the actual terrain surface under the bomb (villages sit on the terrain).
    const groundY = ctx.game.gameScene.heightAt(this.group.position.x, this.group.position.z);
    if (this.group.position.y <= groundY) {
      this.group.position.y = groundY;
      this.detonate(ctx);
      this.dead = true;
    }
  }

  private detonate(ctx: EntityCtx) {
    const pos = this.group.position.clone();
    // Fireball sized to roughly match the blast radius so the visible fire ≈ the lethal zone.
    new Explosion(pos.clone(), 82, 1.3, 'fire').addTo(ctx);
    new Explosion(pos.clone(), 46, 0.7, 'fire').addTo(ctx);
    // Rising smoke column — multiple staggered puffs
    for (let i = 0; i < 4; i++) {
      const off = new THREE.Vector3((Math.random() - 0.5) * 14, 2 + i * 3, (Math.random() - 0.5) * 14);
      new Explosion(pos.clone().add(off), 45 + i * 6, 5 + i, 'smoke')
        .setDrift(new THREE.Vector3((Math.random() - 0.5) * 2, 13, (Math.random() - 0.5) * 2))
        .addTo(ctx);
    }
    ctx.game.audio.playBoom(1.4);
    // Crater the buildings within the blast — destroyed individually (each drops its own rubble and
    // stamps the village's under-attack timer), so the village degrades rather than vanishing.
    const root = ctx.game.gameScene.root;
    for (const v of ctx.game.gameScene.villages) {
      if (v.destroyed || pos.distanceTo(v.position) > BLAST_RADIUS + v.radius) continue;
      for (const b of v.buildings) {
        if (!b.destroyed && pos.distanceTo(b.position) <= BLAST_RADIUS) b.destroy(root);
      }
    }
  }
}
